import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { endpoints } from "@/lib/api";
import { useSettings } from "@/lib/useSettings";
import Icon from "@/components/Icon";
import { Reveal } from "@/lib/motionSafe";
import QuoteFormModal from "@/components/QuoteFormModal";

export default function Services() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalSlug, setModalSlug] = useState("");
  const { settings } = useSettings();

  useEffect(() => {
    endpoints
      .services()
      .then((rows) => setServices(rows.filter((s) => s.is_active !== false)))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const openQuote = (slug = "") => {
    setModalSlug(slug);
    setModalOpen(true);
  };

  return (
    <div data-testid="page-services">
      {/* Compact hero */}
      <section className="aman-hero relative pt-14 pb-16 md:pt-20 md:pb-24 overflow-hidden">
        <div className="aman-glow-orb -top-24 -end-24" aria-hidden="true" />
        <div className="max-w-7xl mx-auto px-6 md:px-10 relative">
          <Reveal>
            <div className="inline-flex items-center gap-2 rounded-full bg-[#00E5FF]/10 border border-[#00E5FF]/40 text-[#00E5FF] px-4 py-1.5 text-xs font-semibold">
              <Icon name="shield-check" size={14} />
              خدماتنا
            </div>
          </Reveal>
          <Reveal delay={100}>
            <h1 className="font-heading mt-5 text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.15]" data-testid="services-hero-title">
              {settings?.services_page_title_ar || "حلولٌ أمنية متكاملة، مصمّمة لك"}
            </h1>
          </Reveal>
          <Reveal delay={200}>
            <p className="mt-5 max-w-2xl text-lg text-slate-300 leading-relaxed">
              {settings?.services_page_desc_ar || "نقدّم أربع خدمات رئيسية تغطي كل احتياجات الحماية والاتصال في منشأتك، من الكاميرات وأنظمة الإنذار إلى إنذار الحريق والشبكات — بتنفيذ احترافي وضمان معتمد."}
            </p>
          </Reveal>
        </div>
      </section>

      {/* Alternating rows */}
      <section className="bg-white py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-10 space-y-20 md:space-y-28">
          {loading && (
            <div className="text-slate-500 text-center" data-testid="services-loading">جاري التحميل...</div>
          )}
          {!loading && services.length === 0 && (
            <div className="text-slate-500 text-center">لا توجد خدمات متاحة حالياً.</div>
          )}
          {services.map((svc, i) => {
            const flip = i % 2 === 1; // alternate
            const topFeatures = (svc.features || []).slice(0, 4);
            return (
              <Reveal key={svc.slug}>
                <article
                  className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-14 items-center"
                  data-testid={`services-row-${svc.slug}`}
                >
                  {/* Image side */}
                  <div className={`lg:col-span-6 ${flip ? "lg:order-2" : ""}`}>
                    <div className="relative rounded-3xl overflow-hidden border border-slate-100 shadow-[0_30px_80px_-40px_rgba(10,22,40,0.35)]">
                      {svc.hero_image ? (
                        <img
                          src={svc.hero_image}
                          alt={svc.title_ar}
                          className="w-full h-[320px] sm:h-[400px] object-cover"
                          loading="lazy"
                        />
                      ) : (
                        <div className="w-full h-[320px] sm:h-[400px] bg-slate-100" />
                      )}
                      <div className="absolute inset-0 bg-gradient-to-tr from-[#0A1628]/60 via-transparent to-transparent" />
                      <div className="absolute top-5 start-5 w-12 h-12 rounded-2xl bg-[#0A1628]/85 backdrop-blur border border-white/10 text-[#00E5FF] flex items-center justify-center">
                        <Icon name={svc.icon} size={24} />
                      </div>
                      <div className="absolute bottom-5 end-5 rounded-full bg-white/95 text-[#0A1628] text-xs font-bold px-3 py-1.5">
                        خدمة #{i + 1}
                      </div>
                    </div>
                  </div>

                  {/* Text side */}
                  <div className={`lg:col-span-6 ${flip ? "lg:order-1" : ""}`}>
                    <span className="aman-tag-dark">{`0${i + 1}`}</span>
                    <h2 className="font-heading mt-4 text-3xl sm:text-4xl font-bold text-[#0A1628] leading-tight">
                      {svc.title_ar}
                    </h2>
                    <p className="mt-4 text-slate-600 leading-loose">{svc.short_desc_ar}</p>

                    {topFeatures.length > 0 && (
                      <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {topFeatures.map((f, k) => (
                          <li key={k} className="flex items-start gap-2.5 text-slate-700 text-sm">
                            <span className="mt-0.5 w-5 h-5 rounded-full bg-[#00E5FF]/15 text-[#00b8cc] flex items-center justify-center shrink-0">
                              <Icon name="check" size={13} strokeWidth={3} />
                            </span>
                            <span>{f}</span>
                          </li>
                        ))}
                      </ul>
                    )}

                    <div className="mt-8 flex flex-wrap gap-3">
                      <Link
                        to={`/services/${svc.slug}`}
                        className="aman-btn-light text-sm"
                        data-testid={`services-row-details-${svc.slug}`}
                      >
                        <Icon name="arrow-left" size={16} strokeWidth={2} />
                        التفاصيل
                      </Link>
                      <button
                        type="button"
                        onClick={() => openQuote(svc.slug)}
                        className="aman-btn-primary text-sm"
                        data-testid={`services-row-quote-${svc.slug}`}
                      >
                        <Icon name="send" size={16} strokeWidth={2} />
                        اطلب عرض سعر
                      </button>
                    </div>
                  </div>
                </article>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* Closing CTA */}
      <section className="bg-[#0A1628] py-16 md:py-20 border-t border-white/5" data-testid="services-cta">
        <div className="max-w-4xl mx-auto px-6 md:px-10 text-center">
          <Reveal>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white leading-tight">
              لست متأكداً أي خدمة تناسبك؟
            </h2>
          </Reveal>
          <Reveal delay={100}>
            <p className="mt-4 text-slate-300 leading-relaxed">
              تواصل مع فريقنا للحصول على استشارة مجانية ودراسة موقعك، وسنقترح الحل الأمثل لميزانيتك واحتياجاتك.
            </p>
          </Reveal>
          <Reveal delay={200}>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <button
                type="button"
                onClick={() => openQuote("")}
                className="aman-btn-primary text-base"
                data-testid="services-cta-quote"
              >
                <Icon name="send" size={18} strokeWidth={2} />
                اطلب استشارة مجانية
              </button>
              {settings?.phone && (
                <a href={`tel:${settings.phone}`} className="aman-btn-secondary text-base">
                  <Icon name="phone" size={18} />
                  اتصل بنا الآن
                </a>
              )}
            </div>
          </Reveal>
        </div>
      </section>

      <QuoteFormModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        defaultServiceSlug={modalSlug}
      />
    </div>
  );
}
